import React, { useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faDownload } from "@fortawesome/free-solid-svg-icons";
import "./style.scss";
import { useAppContext } from "../../../../../../AppContext";

function Products(props) {
  const [braKey, setBraKey] = useState("");
  const [date, setDate] = useState("");
  const [productsData, setProductsData] = useState([]);
  const [rawProducts, setRawProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const { chimeraserv, user } = useAppContext();
  // Get today's date in YYYY-MM-DD format as default
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Flatten product data for table display (only required fields)
  const flattenProductData = (product) => {
    return {
      mfz_id: product.mfz_id || "",
      iso_country_code: product.iso_country_code || "",
      item_id:
        product.item_id != null && !isNaN(product.item_id)
          ? parseInt(product.item_id, 10)
          : 0,
      item_name: product.item_name || "",
      category_id:
        product.category_id != null && !isNaN(product.category_id)
          ? parseInt(product.category_id, 10)
          : 0,
    };
  };

  // Generate CSV from the fetched data (only required fields)
  const generateCSV = (products) => {
    if (!products || products.length === 0) {
      return "";
    }
    const headers = [
      "mfz_id",
      "iso_country_code",
      "item_id",
      "item_name",
      "category_id",
    ];
    const rows = products.map((product) => [
      product.mfz_id || "",
      product.iso_country_code || "",
      product.item_id != null &&
      product.item_id !== "" &&
      !isNaN(product.item_id)
        ? parseInt(product.item_id, 10)
        : 0,
      product.item_name || "",
      product.category_id != null &&
      product.category_id !== "" &&
      !isNaN(product.category_id)
        ? parseInt(product.category_id, 10)
        : 0,
    ]);
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell);
            return cellStr.includes(",") || cellStr.includes('"')
              ? `"${cellStr.replace(/"/g, '""')}"`
              : cellStr;
          })
          .join(",")
      ),
    ].join("\n");
    return csvContent;
  };

  // Fetch products list
  const handleFetchProducts = async (e) => {
    e.preventDefault();
    setError(null);
    setProductsData([]);
    setRawProducts([]);
    setDataFetched(false);

    if (!braKey.trim()) {
      setError("Please enter a Brand Key");
      return;
    }

    setLoading(true);

    try {
      const token = user?.data?.usrKey || chimeraserv.authToken;

      const productDate = date || getTodayDate();
      let url = `${chimeraserv.urlInsightBackendReading}/api/products-list/${braKey}/${chimeraserv.mfzId}?date=${productDate}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      // Extract products data
      if (response.data?.data?.products) {
        const products = Array.isArray(response.data.data.products)
          ? response.data.data.products
          : [response.data.data.products];

        setRawProducts(products);

        // Flatten products for table display
        const flattenedData = products.map((product) =>
          flattenProductData(product)
        );
        setProductsData(flattenedData);
        setDataFetched(true);
      } else if (response.data?.data) {
        // Handle case where products is the data directly
        const products = Array.isArray(response.data.data)
          ? response.data.data
          : [response.data.data];
        setRawProducts(products);
        const flattenedData = products.map((product) =>
          flattenProductData(product)
        );
        setProductsData(flattenedData);
        setDataFetched(true);
      } else {
        setError("No products data found in response");
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Error fetching products"
      );
      console.error("Products fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Download CSV file
  const handleDownloadCSV = () => {
    if (rawProducts.length === 0) {
      setError("No data available for CSV export");
      return;
    }

    try {
      const csvContent = generateCSV(rawProducts);

      // Create blob and trigger download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;

      // Format filename as 88144_products_YYYYMMDD.csv
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0].replace(/-/g, "");
      link.setAttribute(
        "download",
        `${chimeraserv.mfzId}_products_${formattedDate}.csv`
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError("Error generating CSV: " + err.message);
    }
  };

  const handlePushToS3 = async () => {
    if (rawProducts.length === 0) {
      setError("No data available to push to S3");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const token = props.user?.data?.usrKey || chimeraserv.authToken;
      const response = await axios.post(
        `${chimeraserv.urlAwsPusher}/upload/products`,
        { data: rawProducts },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      if (response.data && response.data.success) {
        setError(null);
        alert(
          "Successfully pushed products to S3.\nS3 URL: " + response.data.url
        );
      } else {
        setError(response.data.error || "Failed to push to S3");
      }
    } catch (error) {
      setError(
        error.response?.data?.error ||
          error.message ||
          "Failed to push products to S3"
      );
    } finally {
      setLoading(false);
    }
  };

  // Get table columns (only required fields)
  const getTableColumns = () => {
    return [
      "mfz_id",
      "iso_country_code",
      "item_id",
      "item_name",
      "category_id",
    ];
  };

  // Format column name from snake_case to Title Case
  const formatColumnName = (columnName) => columnName;

  // Format cell value for display
  const formatCellValue = (value, col) => {
    if (col === "item_id" || col === "category_id") {
      if (value === "" || value === null || isNaN(value)) return 0;
      return parseInt(value, 10);
    }
    if (typeof value === "number") {
      return value.toFixed(2);
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return value || "-";
  };

  const columns = getTableColumns();

  return (
    <div className="products-container">
      <div className="products-header">
        <h2>Products List</h2>
        <p>Fetch and download products data from the system</p>
      </div>

      <form onSubmit={handleFetchProducts} className="products-form">
        <div className="form-group">
          <label htmlFor="braKey">Brand Key *</label>
          <input
            type="text"
            id="braKey"
            value={braKey}
            onChange={(e) => setBraKey(e.target.value)}
            placeholder="Enter Brand Key (e.g., BRA001)"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="date">Date</label>
          <input
            type="date"
            id="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <button type="submit" disabled={loading} className="btn-fetch">
          {loading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> Fetching...
            </>
          ) : (
            "Fetch Products"
          )}
        </button>
      </form>

      {error && <div className="alert alert-error">{error}</div>}

      {dataFetched && productsData.length === 0 && (
        <div className="alert alert-warning">No products data found</div>
      )}

      {dataFetched && productsData.length > 0 && (
        <>
          <div className="results-header">
            <h3>Products Data ({productsData.length} records)</h3>
            <button
              onClick={handleDownloadCSV}
              disabled={loading}
              className="btn-download"
            >
              <FontAwesomeIcon icon={faDownload} /> Download CSV
            </button>
            <button
              onClick={handlePushToS3}
              disabled={loading || rawProducts.length === 0}
              className="btn-download"
              style={{ marginLeft: "10px" }}
            >
              <FontAwesomeIcon icon={faDownload} /> Push
            </button>
          </div>

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col}>{formatColumnName(col)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {productsData.map((row, idx) => (
                  <tr key={idx}>
                    {columns.map((col) => (
                      <td key={`${idx}-${col}`}>
                        {formatCellValue(row[col], col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default Products;
